<template>
  <!-- 全站公共头部：Figma Rectangle 346244601（1920x64 玻璃拟态，含搜索框）1:1 还原
       注：header 的 backdrop-filter 会使 fixed 子元素的包含块失效，搜索面板需与 header 同级 -->
  <div class="app-header-root">
    <header class="app-header">
      <div class="app-header__inner">
        <!-- 左：Logo + 导航（788） -->
        <div class="app-header__left">
          <img class="app-header__logo" :src="img('logo_header.png')" alt="Tushare" @click="goHome" />
          <nav class="app-header__nav">
            <a v-for="item in navItems" :key="item.label" class="app-header__nav-item"
              :class="{ 'app-header__nav-item--active': isActive(item) }" :style="itemStyle(item)"
              :href="item.href || 'javascript:void(0);'" @click="onItemClick(item)">
              <span>{{ item.label }}</span>
              <img v-if="isActive(item)" class="app-header__nav-underline" :src="img('nav_underline.svg')" alt="" />
              <!-- 双 11 角标：Figma Group 2033205928，权限中心右上 43x17，循环播放 放大->闹铃晃动；仅 2026-11-03 ~ 2026-11-11 展示 -->
              <img v-if="item.badge && showBadge" class="app-header__nav-badge" :src="badgeImg" alt="11.11" />
            </a>
          </nav>
        </div>

        <!-- 搜索：输入背景框 x1230 y12 180x40 r8，1.5px 渐变描边（hover 流光旋转） -->
        <div class="app-header__search" @click="openSearch">
          <img class="app-header__search-icon" :src="img('search_icon_grad.svg')" alt="" />
          <input v-model="keyword" class="app-header__search-input" type="text" placeholder="搜索" @focus="openSearch"
            @click.stop />
          <img v-if="keyword" class="app-header__search-clear" :src="img('search_clear.png')" alt="清空"
            @click.stop="keyword = ''" />
        </div>

        <!-- 右：登录/注册 或 登录后用户模块 -->
        <div v-if="!userInfo.id" class="app-header__login" @click="toLogin">登录/注册</div>
        <UserInfo v-else />
      </div>
    </header>

    <!-- 搜索激活态：Rectangle 346245122 面板（顶 8 水平居中 900x312，完全覆盖导航搜索框） -->
    <transition name="search-panel">
      <div v-if="searchOpen" class="search-overlay">
        <div class="search-overlay__mask" @click="closeSearch"></div>
        <div class="search-panel"
          :class="{ 'search-panel--ready': searchReady, 'search-panel--with-recent': showRecent, 'search-panel--filled': hasKeyword }">
          <!-- 大输入框：852x48，描边渐变 #002bff->#1051f5->#9225ff，自 180px 向右锚定展开 -->
          <div class="search-panel__inputwrap">
            <input ref="panelInput" v-model="keyword" class="search-panel__input"
              :class="{ 'search-panel__input--clearable': hasKeyword }" type="text" :placeholder="panelPlaceholder"
              @keydown.enter="goSearchPage" @keydown.esc="closeSearch" />
            <!-- 输入非空：清空 X + 分隔线（Figma 搜索中状态） -->
            <template v-if="hasKeyword">
              <img class="search-panel__clear" :src="img('search_close.svg')" alt="清空输入" @click="keyword = ''" />
              <span class="search-panel__divider"></span>
            </template>
            <!-- 搜索图标：以 PNG 作蒙版，颜色随输入态切换（默认灰 / 有内容 #1D2129 与清空 X 一致） -->
            <span class="search-panel__submit" role="button" aria-label="搜索" @click="goSearchPage"></span>
          </div>

          <!-- 空输入态：最近搜索（有历史时）+ 热门搜索 -->
          <template v-if="!hasKeyword">
            <!-- 最近搜索：Frame 2036087562，标题行 + 标签云（描边 1px #e5e6eb r4） -->
            <div v-if="showRecent" class="search-panel__recent">
              <div class="search-panel__recent-head">
                <div class="search-panel__recent-title">
                  <img :src="img('search_clock.svg')" alt="" />
                  <span>最近搜索</span>
                </div>
                <div class="search-panel__clearall" @click="clearRecent">
                  <img :src="img('search_trash.svg')" alt="" />
                  <span>清空</span>
                </div>
              </div>
              <div class="search-panel__recent-tags">
                <span v-for="word in recentSearches" :key="word" class="search-panel__tag" @click="pickRecent(word)">{{
                  shortRecent(word) }}</span>
              </div>
            </div>
            <!-- 热门搜索：Frame 2036087541，标题 22 + 5 行 22 间距 12 -->
            <div class="search-panel__hot">
              <div class="search-panel__hot-head">
                <img :src="img('flame_icon.svg')" alt="" />
                <span>热门搜索</span>
              </div>
              <ul class="search-panel__hot-list">
                <li v-for="(item, i) in hotWords" :key="item.keyword" @click="pickHot(item.keyword)">
                  <em :style="{ color: hotRankColor(i) }">{{ i + 1 }}</em>
                  <span>{{ item.keyword }}</span>
                </li>
              </ul>
            </div>
          </template>

          <!-- 非空输入态：搜索结果列表 / 暂无搜索结果 -->
          <template v-else>
            <ul v-if="searchResults.length" class="search-panel__results">
              <li v-for="item in searchResults" :key="item.id" @click="onResultClick(item)">
                <p class="search-panel__result-title">
                  <template v-for="(seg, si) in highlightSegs(item.title)">
                    <span v-if="seg.hit" :key="'t' + si" class="search-panel__hl">{{ seg.t }}</span>
                    <template v-else>{{ seg.t }}</template>
                  </template>
                </p>
                <p class="search-panel__result-desc">
                  <template v-for="(seg, si) in highlightSegs(item.desc)">
                    <span v-if="seg.hit" :key="'d' + si" class="search-panel__hl">{{ seg.t }}</span>
                    <template v-else>{{ seg.t }}</template>
                  </template>
                </p>
              </li>
            </ul>
            <div v-else-if="searched" class="search-panel__empty">暂无搜索结果</div>
          </template>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import UserInfo from "./userInfo.vue";
import api from "@/api/index";

const ASSETS = "home/";
// 最近搜索本地存储键（不区分登录态）
const RECENT_KEY = "tushare_search_history";
const RECENT_MAX = 10;
// 最近搜索标签最长展示字数，超出以「…」省略
const RECENT_SHOW_MAX = 14;
// 实时搜索防抖时长
const SEARCH_DEBOUNCE = 1000;
// 双 11 角标展示时间（本地时间）：2026-11-03 00:00 ~ 2026-11-11 24:00
const BADGE_START = new Date(2026, 10, 3, 0, 0, 0).getTime();
const BADGE_END = new Date(2026, 10, 12, 0, 0, 0).getTime();

export default {
  name: "AppHeader",
  components: { UserInfo },
  data() {
    return {
      keyword: "",
      // 搜索激活态（下拉面板）
      searchOpen: false,
      searchReady: false,
      // 最近搜索（localStorage 持久化，最新在前，上限 10 条）
      recentSearches: [],
      // 热门搜索词（GET /documents/search/hot_words）
      hotWords: [],
      // 搜索结果（GET /documents/search）
      searchResults: [],
      // 本次搜索结果对应的关键词（用于高亮，避免与输入框实时值错位）
      resultKeyword: "",
      // 双 11 角标图（images 根目录，129x51 @3x，渲染为 Figma 标注的 43x17）
      badgeImg: require("@/assets/images/1111.png"),
      // 当前搜索的 search_id（点击结果时回传给 /documents/search/select）
      searchId: "",
      // 是否已发起过搜索（控制「暂无搜索结果」只在请求返回后展示）
      searched: false,
      // Figma Frame 2036086973：644 = 64+100+96*5，各项宽度/内边距按设计稿
      navItems: [
        { label: "首页", w: 64, px: 16, route: "/" },
        { label: "平台介绍", w: 100, px: 16, href: "/document/1" },
        { label: "数据接口", w: 96, px: 10, href: "/document/2" },
        // { label: "资讯数据", w: 96, px: 16, href: "/news" },
        { label: "数据工具", w: 96, px: 16, href: "/webclient" },
        { label: "权限中心", w: 96, px: 16, route: "/permission", badge: true },
        { label: "活动套餐", w: 96, px: 16, route: "/combo" },
      ],
    };
  },
  computed: {
    userInfo() {
      return this.$store.state.userinfo;
    },
    // 双 11 角标仅在活动期内展示，其余时间默认隐藏
    showBadge() {
      const now = Date.now();
      return now >= BADGE_START && now < BADGE_END;
    },
    // 输入非空（忽略首尾空格）
    hasKeyword() {
      return this.keyword.trim().length > 0;
    },
    // 有历史时展示最近搜索区块
    showRecent() {
      return this.recentSearches.length > 0;
    },
    // 输入框默认回显 Top 1 热词
    panelPlaceholder() {
      const top = this.hotWords[0];
      return (top && top.keyword) || "请输入你想提问的问题";
    },
  },
  watch: {
    // 实时搜索：输入停止 1s 后发起请求
    keyword(val) {
      // 提交搜索时已立即请求，跳过本次防抖，避免重复发请求
      if (this._skipDebounce) {
        this._skipDebounce = false;
        return;
      }
      this.searched = false;
      clearTimeout(this._searchTimer);
      const w = (val || "").trim();
      if (!w) {
        this.searchResults = [];
        this.resultKeyword = "";
        this.searchId = "";
        return;
      }
      this._searchTimer = setTimeout(() => this.fetchSearchResults(w), SEARCH_DEBOUNCE);
    },
  },
  created() {
    this.recentSearches = this.loadRecent();
    this.getHotWords();
  },
  methods: {
    img(name) {
      return require(`@/assets/images/${ASSETS}${name}`);
    },
    itemStyle(item) {
      return { width: item.w + "px", paddingLeft: item.px + "px", paddingRight: item.px + "px" };
    },
    isActive(item) {
      if (!item.route) return false;
      if (item.route === "/") return this.$route.path === "/";
      return this.$route.path.startsWith(item.route);
    },
    onItemClick(item) {
      if (item.href) return; // 站外/文档链接走默认跳转
      if (item.route && this.$route.path !== item.route) {
        this.$router.push(item.route).catch(() => { });
      }
    },
    goHome() {
      if (this.$route.path !== "/") this.$router.push("/").catch(() => { });
    },
    toLogin() {
      this.$router.push({ name: "login" }).catch(() => { });
    },
    // 打开搜索面板：面板淡入 + 输入框自 180px 展开至 852px（阿里云同款节奏）
    openSearch() {
      if (this.searchOpen) return;
      this.searchOpen = true;
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.searchReady = true;
        });
        const el = this.$refs.panelInput;
        if (el) el.focus();
      });
      document.addEventListener("keydown", this.onSearchEsc);
    },
    // 关闭面板（遮罩 / ESC / 结果点击后）：同时清空输入与结果，避免下次打开残留上次搜索词
    closeSearch() {
      this.searchOpen = false;
      this.searchReady = false;
      clearTimeout(this._searchTimer);
      this.keyword = "";
      this.searchResults = [];
      this.resultKeyword = "";
      this.searchId = "";
      this.searched = false;
      document.removeEventListener("keydown", this.onSearchEsc);
    },
    onSearchEsc(e) {
      if (e.key === "Escape") this.closeSearch();
    },
    pickHot(word) {
      // 点击热门词：回显 + 发起搜索（由 submitSearch 统一处理）
      this.submitSearch(word);
    },
    // ===== 热门词 =====
    getHotWords() {
      api
        .getHotWords()
        .then((res) => {
          const { code, data } = res || {};
          if (code !== 0 || !data || !Array.isArray(data.datas)) return;
          this.hotWords = data.datas.filter((it) => it && it.keyword);
        })
        .catch(() => { });
    },
    // ===== 搜索提交与最近搜索历史 =====
    // 回车 / 点击搜索按钮：新窗口跳转文档搜索页，携带 URL 编码后的关键词 q
    goSearchPage() {
      const w = (this.keyword || "").trim() || (this.hotWords[0] && this.hotWords[0].keyword) || "";
      if (!w) return;
      window.open("https://tushare.pro/document/search?q=" + encodeURIComponent(w), "_blank");
    },
    // 即时搜索（随输入/点热词触发，用于面板内展示结果且写入最近搜索，见 fetchSearchResults）
    submitSearch(word) {
      // 空输入时使用 placeholder 回显的 Top 1 热词
      const w = (word || "").trim() || (this.hotWords[0] && this.hotWords[0].keyword) || "";
      if (!w) return;
      clearTimeout(this._searchTimer);
      if (this.keyword !== w) {
        this._skipDebounce = true;
        this.keyword = w;
      }
      this.fetchSearchResults(w);
    },
    // 请求搜索结果：source 取当前路由 meta.name
    fetchSearchResults(word) {
      const w = (word || "").trim();
      if (!w) return;
      const seq = (this._reqSeq = (this._reqSeq || 0) + 1);
      const meta = (this.$route && this.$route.meta) || {};
      api
        .getSearchResults({
          keyword: w,
          source: meta.name || "",
          skip: 0,
          limit: 10,
        })
        .then((res) => {
          // 仅采用最后一次请求的结果，避免快速输入时旧响应覆盖新结果
          if (seq !== this._reqSeq) return;
          const { code, data } = res || {};
          // 最近搜索：只要有响应就写入本地，不再依据后端返回条数（0 条也记录）
          this.addRecent(w);
          if (code !== 0 || !data) {
            this.searchResults = [];
            this.searchId = "";
            this.resultKeyword = w;
            this.searched = true;
            return;
          }
          this.searchId = data.search_id || "";
          this.resultKeyword = w;
          this.searchResults = (data.datas || []).map((it) => ({
            id: it.id,
            title: it.title || "",
            desc: this.cleanText(it.content),
            url: it.doc_url || "",
          }));
          this.searched = true;
        })
        .catch(() => {
          if (seq !== this._reqSeq) return;
          this.searchResults = [];
          this.searchId = "";
          this.resultKeyword = w;
          this.searched = true;
        });
    },
    // 富文本片段转纯文本（去 HTML 标签、实体与多余空白），保证高亮基于纯文本
    cleanText(str) {
      return String(str || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/\s+/g, " ")
        .trim();
    },
    // 关键词命中拆分：[{t, hit}]，供模板渲染高亮片段
    highlightSegs(text) {
      const src = String(text || "");
      const kw = this.resultKeyword;
      if (!kw) return [{ t: src, hit: false }];
      const lowerSrc = src.toLowerCase();
      const lowerKw = kw.toLowerCase();
      const out = [];
      let i = 0;
      while (i < src.length) {
        const idx = lowerSrc.indexOf(lowerKw, i);
        if (idx === -1) {
          out.push({ t: src.slice(i), hit: false });
          break;
        }
        if (idx > i) out.push({ t: src.slice(i, idx), hit: false });
        out.push({ t: src.slice(idx, idx + kw.length), hit: true });
        i = idx + kw.length;
      }
      return out;
    },
    // 点击最近搜索标签：回显 + 重新搜索
    pickRecent(word) {
      this.submitSearch(word);
    },
    // 点击搜索结果：新开接口文档 + 上报点击行为；0.5s 后收起面板并清空输入
    onResultClick(item) {
      if (item.url) window.open(item.url, "_blank");
      if (this.searchId) {
        api
          .selectSearchResult({ doc_id: item.id, search_id: this.searchId })
          .catch(() => { });
      }
      clearTimeout(this._closeTimer);
      this._closeTimer = setTimeout(() => {
        this.closeSearch();
        this.keyword = "";
      }, 500);
    },
    // 最近搜索标签展示文案：超过 14 字以「…」省略
    shortRecent(word) {
      const w = String(word || "");
      return w.length > RECENT_SHOW_MAX ? w.slice(0, RECENT_SHOW_MAX) + "…" : w;
    },
    // 写入历史：去重置顶，上限 10 条（超出时丢弃最旧记录）
    addRecent(word) {
      const list = this.recentSearches.filter((item) => item !== word);
      list.unshift(word);
      this.recentSearches = list.slice(0, RECENT_MAX);
      this.saveRecent();
    },
    // 清空全部历史
    clearRecent() {
      this.recentSearches = [];
      this.saveRecent();
    },
    loadRecent() {
      try {
        const raw = localStorage.getItem(RECENT_KEY);
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list.filter((item) => typeof item === "string" && item) : [];
      } catch (e) {
        return [];
      }
    },
    saveRecent() {
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(this.recentSearches));
      } catch (e) {
        /* 隐私模式等写入失败时静默降级，历史仅在内存中生效 */
      }
    },
    // Figma 榜序色：1 #fc3e4f / 2 #ff7d00 / 3 #ffb44a / 其余 #a2abb9
    hotRankColor(i) {
      return ["#fc3e4f", "#ff7d00", "#ffb44a"][i] || "#a2abb9";
    },
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onSearchEsc);
    clearTimeout(this._searchTimer);
    clearTimeout(this._closeTimer);
  },
};
</script>

<style lang="scss" scoped>
$font: "Source Han Sans SC";
$brand: #3461ff;
$text-6: #1d2129;
$text-5: #4e5969;
$text-4: #86909c;

.app-header {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  height: 64px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  z-index: 20;
  font-family: $font;
  border-bottom: 1px solid #DFE3EB;


  &,
  & * {
    box-sizing: border-box;
  }

  &__inner {
    display: flex;
    align-items: center;
    margin: 0 auto;
    width: 1200px;
    height: 64px;
  }

  &__left {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    width: 788px;
    height: 64px;
  }

  &__logo {
    width: 128px;
    height: 64px;
    cursor: pointer;
  }

  &__nav {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    width: 644px;
    height: 64px;
  }

  /* 用后代选择器提升优先级，防止被宿主页的 :where(a){color:inherit} 类规则覆盖 */
  & .app-header__nav-item {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    height: 64px;
    line-height: 24px;
    letter-spacing: 0;
    color: $text-6;
    font-size: 16px;
    font-weight: 400;
    text-decoration: none;
    cursor: pointer;
    transition: color 0.25s ease;

    &:hover {
      color: $brand;
    }

    &--active {
      font-weight: 500;

      &:hover {
        color: $text-6;
      }
    }
  }

  &__nav-underline {
    position: absolute;
    top: 54px;
    left: 50%;
    transform: translateX(-50%);
    width: 24px;
    height: 2px;
  }

  /* 双 11 角标：Figma Group 2033205928（导航容器内 x526 y6 43x17），
     相对权限中心项即右上 right:-21px top:6px；动画循环：微放大 -> 明显闹铃晃动 -> 停留 */
  &__nav-badge {
    position: absolute;
    top: 6px;
    right: -21px;
    width: 43px;
    height: 17px;
    will-change: transform;
    animation: nav-badge-shake 1.8s ease-in-out infinite;
  }

  /* Figma 输入背景框：默认 1.5px 渐变描边（90deg：#baabff 0% → #9aadff 35.34% → #e3baf3 62.5% → #d4dfff 96.15%）；
     hover 升级为同色系流光旋转边框（conic 渐变持续旋转，mask 描边技术实现，不占布局） */
  &__search {
    position: relative;
    display: flex;
    align-items: center;
    column-gap: 4px;
    flex-shrink: 0;
    margin-left: 82px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.5);
    padding-right: 18px;
    padding-left: 16px;
    width: 180px;
    height: 40px;
    cursor: text;

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: 8px;
      padding: 1.5px;
      border: 1px solid #EDEEF0;
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask-composite: exclude;
      pointer-events: none;
    }

    &:hover::before {
      background: conic-gradient(from var(--sb-angle), #baabff, #9aadff, #e3baf3, #d4dfff, #baabff);
      animation: sb-flow 1.2s linear infinite;
    }
  }

  &__search-icon {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
  }

  &__search-input {
    border: none;
    outline: none;
    background: transparent;
    padding: 0;
    width: 95px;
    line-height: 24px;
    letter-spacing: 0;
    color: $text-6;
    font-family: $font;
    font-size: 16px;
  }

  &__search-clear {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  &__login {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-left: 16px;
    border-radius: 100px;
    background: $brand;
    width: 134px;
    height: 40px;
    line-height: 24px;
    letter-spacing: 0;
    color: #ffffff;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.25s ease;

    &:hover {
      background: #2550e8;
    }
  }

}

/* 窄屏降级：<1250 隐藏搜索框，<980 隐藏导航项，保证无样式错乱 */
@media (max-width: 1250px) {
  .app-header {
    &__inner {
      padding: 0 24px;
      width: 100%;
    }

    &__search {
      display: none;
    }

    &__login,
    .user-info {
      margin-left: auto;
    }
  }
}

@media (max-width: 980px) {
  .app-header {
    &__nav {
      display: none;
    }

    &__left {
      width: auto;
    }
  }
}

/* 占位符颜色：提升优先级，覆盖宿主页 .home-page input::placeholder 规则 */
.app-header .app-header__search-input::placeholder {
  color: #86909c;
}

/* 流光角度自定义属性（Chrome 85+/Firefox 128+/Safari 16.4+ 平滑插值，低版本退化为静态渐变） */
@property --sb-angle {
  syntax: "<angle>";
  initial-value: 0deg;
  inherits: false;
}

@keyframes sb-flow {
  to {
    --sb-angle: 360deg;
  }
}

/* 双 11 角标动效：0-26% 从小放大（微变化，带回弹收敛）；
   26-74% 闹铃式左右晃动（幅度明显，逐次衰减）；74-100% 停留后循环 */
@keyframes nav-badge-shake {
  0% {
    transform: scale(0.75) rotate(0deg);
  }

  18% {
    transform: scale(1.08) rotate(0deg);
  }

  26% {
    transform: scale(1) rotate(0deg);
  }

  34% {
    transform: scale(1) rotate(-16deg);
  }

  42% {
    transform: scale(1) rotate(13deg);
  }

  50% {
    transform: scale(1) rotate(-10deg);
  }

  58% {
    transform: scale(1) rotate(8deg);
  }

  66% {
    transform: scale(1) rotate(-4deg);
  }

  74% {
    transform: scale(1) rotate(0deg);
  }

  100% {
    transform: scale(1) rotate(0deg);
  }
}

/* ===================== 搜索激活态面板（Rectangle 346245122） ===================== */
.search-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  font-family: $font;
  z-index: 200;

  &,
  & * {
    box-sizing: border-box;
  }

  /* 透明遮罩：仅承接点击关闭，设计稿无底色遮罩 */
  &__mask {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
}

.search-panel {
  position: absolute;
  /* 顶部上延至 8px、完全遮挡背后的导航搜索框（y12 起）；高度随状态内容自适应 */
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  border: 1px solid transparent;
  border-radius: 16px;
  background: linear-gradient(#ffffff, #ffffff) padding-box,
    linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%) border-box;
  box-shadow: 4px 0 12px rgba(0, 0, 0, 0.08);
  padding: 24px;
  width: 900px;

  /* 大输入框：852x48，自 180px 右锚定展开（0.3s 标准缓动，对齐阿里云节奏） */
  &__inputwrap {
    position: relative;
    margin-left: auto;
    width: 180px;
    height: 48px;
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &--ready &__inputwrap {
    width: 852px;
  }

  &__input {
    border: 1px solid transparent;
    border-radius: 8px;
    outline: none;
    background: linear-gradient(#ffffff, #ffffff) padding-box,
      linear-gradient(306.5deg, #002bff 0%, #1051f5 60.73%, #9225ff 100%) border-box;
    padding: 0 36px 0 12px;
    width: 100%;
    height: 48px;
    line-height: 24px;
    letter-spacing: 0;
    color: $text-6;
    font-family: $font;
    font-size: 16px;
    caret-color: #1d2129;

    &::placeholder {
      color: #86909c;
    }

    /* 输入非空：右侧让位给 清空X + 分隔线 + 搜索图标 */
    &--clearable {
      padding-right: 78px;
    }
  }

  /* 搜索图标：PNG 作蒙版 + 背景色着色（形状沿用设计稿，颜色可随状态切换） */
  &__submit {
    position: absolute;
    top: 16px;
    right: 12px;
    width: 16px;
    height: 16px;
    background-color: #86909c;
    -webkit-mask: url("~@/assets/images/home/search_icon_submit.png") no-repeat center / 16px 16px;
    mask: url("~@/assets/images/home/search_icon_submit.png") no-repeat center / 16px 16px;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  /* 输入有内容：搜索图标与清空 X 同色 #1D2129 */
  &--filled &__submit {
    background-color: #1d2129;
  }

  /* 输入非空态右侧簇：清空X（16x16）+ 分隔线（1x12 #e5e6eb），间距均 12 */
  &__clear {
    position: absolute;
    top: 16px;
    right: 53px;
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  &__divider {
    position: absolute;
    top: 18px;
    right: 40px;
    background: #e5e6eb;
    width: 1px;
    height: 12px;
  }

  /* 热门搜索：标题淡入略延迟，跟随输入框展开节奏 */
  &__hot {
    margin-top: 16px;
    opacity: 0;
    transition: opacity 0.2s ease 0.15s;
  }

  /* 最近搜索 / 结果列表 / 空状态：与热门同款淡入节奏 */
  &__recent,
  &__results,
  &__empty {
    opacity: 0;
    transition: opacity 0.2s ease 0.15s;
  }

  &--ready &__hot,
  &--ready &__recent,
  &--ready &__results,
  &--ready &__empty {
    opacity: 1;
  }

  &__hot-head {
    display: flex;
    align-items: center;
    height: 22px;

    img {
      width: 13px;
      height: 13px;
    }

    span {
      margin-left: 4px;
      line-height: 24px;
      letter-spacing: 0;
      color: #4e5969;
      font-size: 14px;
      font-weight: 400;
    }
  }

  &__hot-list {
    margin: 12px 0 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      align-items: center;
      height: 22px;
      cursor: pointer;

      &+li {
        margin-top: 12px;
      }

      em {
        width: 8px;
        line-height: 22px;
        letter-spacing: 0;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
      }

      span {
        margin-left: 8px;
        line-height: 22px;
        letter-spacing: 0;
        color: #1d2129;
        font-size: 14px;
        font-weight: 400;
      }
    }
  }

  /* 最近搜索 + 热门搜索 组合态：热门榜单按 Figma 最近搜索页口径（序号 16px、行高 24、间距 12） */
  &--with-recent &__hot-list li {
    height: 24px;

    em {
      width: 10px;
      line-height: 24px;
      font-size: 16px;
    }

    span {
      margin-left: 12px;
      line-height: 24px;
    }
  }

  /* ===== 最近搜索（Figma Frame 2036087562） ===== */
  &__recent {
    margin-top: 16px;
  }

  &__recent-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 22px;
  }

  &__recent-title {
    display: flex;
    align-items: center;

    img {
      width: 13px;
      height: 13px;
    }

    span {
      margin-left: 4px;
      line-height: 22px;
      letter-spacing: 0;
      color: #4e5969;
      font-size: 14px;
      font-weight: 400;
    }
  }

  /* 清空按钮：垃圾桶 16x16 + 文案，间距 3 */
  &__clearall {
    display: flex;
    align-items: center;
    cursor: pointer;

    img {
      width: 16px;
      height: 16px;
    }

    span {
      margin-left: 3px;
      line-height: 19.6px;
      letter-spacing: 0;
      color: #4e5969;
      font-size: 14px;
      font-weight: 400;
    }

    &:hover span {
      color: #1d2129;
    }
  }

  /* 标签云：描边 1px #e5e6eb、r4、高 32、左右内边距 12、间距 12；文字超出可换行 */
  &__recent-tags {
    display: flex;
    flex-wrap: wrap;
    margin-top: 12px;
    gap: 12px;
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    box-sizing: border-box;
    border: 1px solid #e5e6eb;
    border-radius: 4px;
    padding: 5px 12px;
    max-width: 262px;
    min-height: 32px;
    letter-spacing: 0;
    color: #4e5969;
    font-size: 14px;
    font-weight: 400;
    line-height: 19.6px;
    white-space: normal;
    word-break: break-all;
    cursor: pointer;
    transition: border-color 0.2s ease, color 0.2s ease;

    &:hover {
      border-color: #c9cdd4;
      color: #1d2129;
    }
  }

  /* ===== 搜索结果列表（Figma Frame 2036087558：852 宽，标题+双行描述，间距 16） ===== */
  &__results {
    margin: 16px 0 0;
    padding: 0;
    list-style: none;
    /* 结果区高度固定：超出时在面板内纵向滚动，不撑开整体布局 */
    max-height: 320px;
    overflow-y: auto;
    overscroll-behavior: contain;

    /* 滚动条：去掉轨道底色与上下箭头，仅保留细滑块
       注意：scrollbar-width / scrollbar-color 在 Chromium 121+ 会接管滚动条，
       导致 ::-webkit-scrollbar 系列失效（Windows 下还会带出上下箭头），
       因此这两个标准属性只给 Firefox 用 */
    @supports (-moz-appearance: none) {
      scrollbar-width: thin;
      scrollbar-color: #c9cdd4 transparent;
    }

    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }

    &::-webkit-scrollbar-track,
    &::-webkit-scrollbar-corner {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 3px;
      background: #c9cdd4;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #a2abb9;
    }

    /* 隐藏上下箭头按钮（Windows 经典滚动条） */
    &::-webkit-scrollbar-button {
      display: none;
      width: 0;
      height: 0;
      background: transparent;
    }

    li {
      cursor: pointer;

      &+li {
        margin-top: 16px;
      }
    }
  }

  &__result-title {
    margin: 0;
    line-height: 22px;
    letter-spacing: 0;
    color: #1d2129;
    font-size: 14px;
    font-weight: 400;
  }

  &__result-desc {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    margin: 4px 0 0;
    line-height: 20px;
    letter-spacing: 0;
    color: #86909c;
    font-size: 12px;
    font-weight: 400;
  }

  /* 关键词命中高亮：品牌蓝 + 中等字重 */
  &__hl {
    color: $brand;
    font-weight: 500;
  }

  /* ===== 暂无搜索结果（Figma Group 2033205776，面板内居中） ===== */
  &__empty {
    padding-top: 24px;
    text-align: center;
    line-height: 22px;
    letter-spacing: 0;
    color: #86909c;
    font-size: 14px;
    font-weight: 400;
  }
}

/* 面板展开/收起过渡（0.25s ease，与导航动效一致） */
.search-panel-enter-active,
.search-panel-leave-active {
  transition: opacity 0.25s ease;

  .search-panel {
    transition: opacity 0.25s ease, transform 0.25s ease;
  }
}

.search-panel-enter,
.search-panel-leave-to {
  opacity: 0;

  .search-panel {
    transform: translateX(-50%) translateY(-8px);
  }
}
</style>
